import { useEffect, useRef } from 'react';
import { createChart, CandlestickSeries } from 'lightweight-charts';
import { getValidToken } from '../utils/auth';
import {
  SESSION_TF, SessionBandPrimitive, buildSessionBands,
  DayLinePrimitive, buildDayLines,
  baseChartOptions, TfButtons, SessionLegend, attachMousePan,
} from '../utils/chartUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

const POLL_MS = {
  '1m': 30000, '5m': 60000, '15m': 120000,
  '30m': 180000, '1h': 300000, '4h': 600000, '1D': 900000,
};

// tf/setTf are shared with CapSeriesChart via Options.js.
// symbol/setSymbol/tickers control which stock is shown in this chart.
export default function ChartWidget({ tf, setTf, symbol = 'SPY', setSymbol, tickers = ['SPY'] }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const seriesRef    = useRef(null);
  const primitiveRef = useRef(null);
  const dayLineRef   = useRef(null);
  const pollRef      = useRef(null);
  const mountedRef   = useRef(true);
  const initialDone  = useRef(false);

  // Create chart once on mount
  useEffect(() => {
    mountedRef.current = true;

    const chart = createChart(containerRef.current, baseChartOptions(containerRef.current.clientWidth || 300));
    chartRef.current = chart;

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', downColor: '#ef5350',
      borderUpColor: '#26a69a', borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', wickDownColor: '#ef5350',
    });
    seriesRef.current = series;

    const primitive = new SessionBandPrimitive();
    primitiveRef.current = primitive;
    series.attachPrimitive(primitive);

    // Attach day-line primitive AFTER session bands so it renders on top of them.
    const dayLine = new DayLinePrimitive();
    dayLineRef.current = dayLine;
    series.attachPrimitive(dayLine);

    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w > 0 && mountedRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: w });
      }
    });
    ro.observe(containerRef.current);

    const detachPan = attachMousePan(containerRef.current, chart);

    return () => {
      mountedRef.current = false;
      detachPan();
      ro.disconnect();
      clearInterval(pollRef.current);
      chart.remove();
      chartRef.current = seriesRef.current = primitiveRef.current = dayLineRef.current = null;
    };
  }, []);

  // Refetch when timeframe or symbol changes
  useEffect(() => {
    initialDone.current = false;
    clearInterval(pollRef.current);

    const fetchBars = async (isInitial) => {
      if (!mountedRef.current || !seriesRef.current) return;
      try {
        const token = await getValidToken();
        if (!token || !mountedRef.current) return;

        const res = await fetch(`${API_URL}/api/chart/bars?symbol=${symbol}&tf=${tf}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || !mountedRef.current) return;

        const bars = await res.json();
        if (!bars || !bars.length || !mountedRef.current) return;

        if (isInitial || !initialDone.current) {
          seriesRef.current.setData(bars);
          chartRef.current.timeScale().scrollToRealTime();
          initialDone.current = true;
        } else {
          seriesRef.current.update(bars[bars.length - 1]);
        }

        if (SESSION_TF.has(tf)) {
          primitiveRef.current?.setBands(buildSessionBands(bars));
        } else {
          primitiveRef.current?.clearBands();
        }

        dayLineRef.current?.setLines(buildDayLines(bars));
      } catch { /* stale data shown until next poll */ }
    };

    fetchBars(true);
    pollRef.current = setInterval(() => fetchBars(false), POLL_MS[tf] || 30000);
    return () => clearInterval(pollRef.current);
  }, [tf, symbol]);

  const isSpx = symbol === 'SPX';

  return (
    <div className="bg-gray-900 rounded-2xl p-5 mb-5">
      <div className="flex items-center gap-3 mb-3 flex-wrap">

        {/* Symbol dropdown */}
        <div className="relative">
          <select
            value={symbol}
            onChange={e => setSymbol?.(e.target.value)}
            className="appearance-none text-xs font-bold rounded px-3 py-1.5 pr-7 border cursor-pointer focus:outline-none focus:border-blue-500"
            style={{ backgroundColor: '#111827', color: '#e5e7eb', borderColor: '#374151' }}
          >
            {tickers.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
        </div>

        {/* SPX note — prices are SPY×10 proxy */}
        {isSpx && (
          <span className="text-xs text-gray-500">~SPY×10</span>
        )}

        <TfButtons tf={tf} setTf={setTf} />
        {SESSION_TF.has(tf) && <SessionLegend />}
      </div>
      <div ref={containerRef} />
    </div>
  );
}
